import React from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { useQuery } from 'react-query';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { HttpClient } from 'apiclient/ApiClient';
import { IOrganizationReportData } from 'models/IOrganizationStats';
import ReportHeader from 'ui/components/shared-components/ReportHeader';
import ClassComparisonChart from 'ui/components/ClassComparisonChart';
import StatTable, { StatColumn } from 'ui/components/StatTable';
import { IOutletContext } from 'ui/components/Dashboard';
import './TeacherStats.css';

const OrganizationStats: React.FC = () => {
    const { reportId } = useParams<{ reportId: string }>();
    const navigate = useNavigate();
    const { setBackButtonProps } = useOutletContext<IOutletContext>();
    const httpClient = HttpClient();
    
    React.useEffect(() => {
        setBackButtonProps({
            onBack: () => navigate('/organization-reports'),
            backType: 'all-reports'
        });
        
        return () => {
            setBackButtonProps({});
        };
    }, [navigate, setBackButtonProps]);

    const organizationQuery = useQuery<IOrganizationReportData>(
        ['organization_report', reportId],
        async (): Promise<IOrganizationReportData> => {
            const response = await httpClient.get(`/organization-reports/${reportId}`);
            return (response as unknown) as IOrganizationReportData;
        },
        { enabled: !!reportId }
    );

    if (organizationQuery.isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (organizationQuery.error) {
        return (
            <Box p={3}>
                <Alert severity="error">
                    Error loading organization statistics. Please try again later.
                </Alert>
            </Box>
        );
    }

    const data = organizationQuery.data;
    if (!data || data.error) {
        return (
            <Box p={3}>
                <Alert severity="error">
                    {data?.error || "No data available for this organization report."}
                </Alert>
            </Box>
        );
    }
    
    if (!data.class_reports) {
        return (
            <Box p={3}>
                <Alert severity="info">
                    No data available for this organization report.
                </Alert>
            </Box>
        );
    }

    // Show all classes (including empty ones), sort by tasks_solved
    const sortedClasses = [...data.class_reports].sort((a, b) => b.tasks_solved - a.tasks_solved);

    // Calculate summary statistics
    const totalTasksSolved = sortedClasses.reduce((sum, cls) => sum + cls.tasks_solved, 0);
    const totalActiveStudents = sortedClasses.reduce((sum, cls) => sum + cls.active_students, 0);


    // Table data preparation
    const tableData = sortedClasses.map(cls => ({
        class_name: cls.class_name,
        tasks_solved: cls.tasks_solved > 0 ? cls.tasks_solved : '—',
        think_rank: cls.think_rank > 0 ? cls.think_rank : '—',
        challenge_score: cls.challenge_score > 0 ? cls.challenge_score : '—',
        active_students: cls.active_students > 0 ? cls.active_students : '—',
        time_per_student: cls.time_per_student > 0 ? cls.time_per_student : '—',
        report_link: 'View Report',
        report_id: cls.report_id
    }));

    // Table column definitions
    const tableColumns: StatColumn[] = [
        { key: 'class_name', title: 'Class Name', type: 'text' },
        { key: 'tasks_solved', title: 'Tasks Solved', type: 'number' },
        { key: 'think_rank', title: 'ThinkRank', type: 'percentage' },
        { key: 'challenge_score', title: 'Challenge Score', type: 'number' },
        { key: 'active_students', title: 'Active Students', type: 'number' },
        { key: 'time_per_student', title: 'Time per Student', type: 'number' },
        { 
            key: 'report_link', 
            title: 'Full Report', 
            type: 'link',
            linkHandler: (row) => {
                if (row.report_id) {
                    navigate(`/teacher_stat/${row.report_id}`, { 
                        state: { 
                            fromOrganization: true, 
                            orgReportId: reportId 
                        } 
                    });
                }
            }
        }
    ];

    return (
        <div className="teacher-stats-page">
            {/* Header */}
            <ReportHeader 
                type="organization"
                schoolName={data.organization_name}
                startDate={data.start_date}
                endDate={data.end_date}
            />

            {/* Content */}
            <div className="content">

                {/* Overall Statistics Section - moved to first place */}
                <div className="section">
                    <h2 className="section-title">1. Overall Statistics</h2>
                    <div className="overview-grid">
                        {[
                            { label: 'Total Classes', value: sortedClasses.length.toString() },
                            { label: 'Total Tasks Solved', value: totalTasksSolved.toLocaleString() },
                            { label: 'Total Active Students', value: totalActiveStudents.toString() }
                        ].map((metric, index, array) => (
                            <div 
                                key={metric.label}
                                className="metric-row"
                                style={{
                                    borderBottom: index < array.length - 1 ? '1px solid #e5e7eb' : 'none'
                                }}
                            >
                                <span className="metric-label">{metric.label}</span>
                                <span className="metric-value">{metric.value}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Class Comparison Chart */}
                <div className="section">
                    <h2 className="section-title">2. Class Comparison</h2>
                    {totalTasksSolved === 0 ? (
                        <div style={{ 
                            color: '#111827', 
                            fontSize: '16px', 
                            padding: '20px 0',
                            textAlign: 'left' as const
                        }}>
                            No data for this period
                        </div>
                    ) : (
                        <ClassComparisonChart classes={sortedClasses} />
                    )}
                </div>

                {/* School Summary Table */}
                <div className="table-section">
                    <h2 className="section-title">3. School Summary at a Glance</h2>
                    <StatTable 
                        columns={tableColumns} 
                        data={tableData} 
                    />
                    <div className="description">
                        <strong>ThinkRank</strong> is a dynamic scoring system that reflects a student's skill proficiency. It is designed to measure progress, mastery, and learning patterns.
                        <br /><br />
                        The score combines three aspects of a student’s activity during the period:<br />
                        &bull; <b>Engagement</b> – how many tasks the student completes<br />
                        &bull; <b>Accuracy</b> – how correctly the student solves problems<br />
                        &bull; <b>Challenge level</b> – how complex the tasks they attempt are<br />
                        The score ranges from <b>1 to 100</b>, with higher values indicating stronger performance and engagement with more challenging tasks.
                    </div>
                </div>

                {/* Individual Class Reports Section */}
                <div className="section">
                    <h2 className="section-title">4. Individual Class Reports</h2>
                    <div className="overview-grid">
                        {sortedClasses.map((cls) => (
                            <div 
                                key={cls.class_uid}
                                className="metric-row"
                                style={{ 
                                    borderBottom: 'none',
                                    cursor: cls.report_id ? 'pointer' : 'default'
                                }}
                                onClick={() => {
                                    if (cls.report_id) {
                                        navigate(`/teacher_stat/${cls.report_id}`, { 
                                            state: { 
                                                fromOrganization: true, 
                                                orgReportId: reportId 
                                            } 
                                        });
                                    }
                                }}
                            >
                                <span className="metric-label" style={{ 
                                    color: cls.report_id ? '#6A56CC' : '#666',
                                    textDecoration: cls.report_id ? 'none' : 'none'
                                }}>
                                    → {cls.class_name} ({cls.active_students} students)
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default OrganizationStats;